<script setup>
/**
 * One piece of wording — written here and sent to WhatsApp for approval, or changed after
 * WhatsApp already holds it.
 *
 * ── THE COMPOSER, REBUILT AROUND META'S OWN SHAPE (2026-09-25) ───────────────────────
 *
 * The form was right about the rules and wrong about when it said them. Fourteen inputs, nine of
 * them conditional, every rule held back until the Save button, and a twenty-four hour wait at
 * WhatsApp behind the ones this screen never checked at all. Four things changed:
 *
 *   1. THE RULES FIRE AS SHE TYPES. `validateDraft` runs on every keystroke and `typingErrors`
 *      decides what it is fair to SAY — a field she has not touched is not a mistake, and a
 *      `{{` on its way to `{{1}}` is not a mistake either. Submit is unchanged: it still runs
 *      the full gate, and the two share one rule set, so nothing can be shown here and accepted
 *      there, or the reverse.
 *   2. EVERY BOX COUNTS. Meta's ceiling is on the control as `maxlength` and above it as a live
 *      count, so the limits stop being something to discover. A value that arrives over the
 *      ceiling from WhatsApp Manager is drawn in ruby with its number rather than truncated.
 *   3. THE COLLAPSE IS ANNOUNCED BEFORE IT HAPPENS. Choosing a sign-in code removes seven fields
 *      and adds two. The picker now says what that will cost — by name, only for parts she has
 *      actually written — and she confirms it. Nothing is lost either way; the draft keeps every
 *      field and picking the old category back restores the form. That promise is made because
 *      it is true, which is more than the task editor's equivalent could say.
 *   4. META'S ORDER, TOP TO BOTTOM. Identity, then HEADER → BODY (and its examples) → FOOTER →
 *      BUTTONS, then how long to keep trying, then the preview. The one control that is neither
 *      identity nor content — "how to write the blanks" — is gone from the create form: it is
 *      read off the blanks she writes (`inferFormat`), because that is what the field means.
 *
 * ── THE FOUR RULES THAT ARE NOT OURS (the edit path) ─────────────────────────────────
 *
 *   1. THE NAME AND THE LANGUAGE CANNOT CHANGE. They are the template's identity; Meta does not
 *      accept either on the edit endpoint. Both render locked, with the reason.
 *   2. THE CATEGORY CANNOT CHANGE ON AN APPROVED TEMPLATE. So it is editable while refused or
 *      paused — which is exactly when an owner needs it.
 *   3. AN EDIT REPLACES EVERY COMPONENT. That is why `templateToDraft` reads every field back
 *      including the example values: a field the form fails to load is a field DELETED.
 *   4. AN EDIT RE-OPENS REVIEW, ten times in 30 days and once in 24 hours.
 *
 * DELETION IS NOT HERE. It lives on the list row's facts panel behind a typed confirmation.
 *
 * ── THE PREVIEW IS THE PRODUCT ──────────────────────────────────────────────────────
 *
 * It is `components/WasloTemplatePreview.vue`, fed the draft projected onto Meta's own template
 * shape. It used to be Chatwoot's shared `TemplatePreview`, on the argument that one renderer
 * cannot drift from itself. It drifted from WhatsApp instead: for a template with buttons it
 * drops the header and the footer, and it draws Arabic left to right (spec P-4.7). So this screen
 * now has its own, which draws every part in Meta's order and sets the direction; Chatwoot's is
 * untouched and still renders templates everywhere else. What the composer adds beside it is the
 * one thing no preview can say: which part is over Meta's limit, and by how much.
 *
 * ── WHAT IT SAYS WHEN IT IS DONE ────────────────────────────────────────────────────
 *
 * Whatever Meta said, and nothing else. On a create, `status` may be null and a null renders as
 * "WhatsApp has not told us yet". On an EDIT, Meta answers `{ success: true }` and says nothing
 * about the new status, so this claims nothing about it either.
 */
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import { frontendURL } from 'dashboard/helper/URLHelper';

import TemplateButtonList from '../components/TemplateButtonList.vue';
import TemplateCategoryPicker from '../components/TemplateCategoryPicker.vue';
import TemplateGoalStart from '../components/TemplateGoalStart.vue';
import TemplateQualityChip from '../components/TemplateQualityChip.vue';
import TemplateStatusChip from '../components/TemplateStatusChip.vue';
import WasloInput from '../components/WasloInput.vue';
import WasloPageLayout from '../components/WasloPageLayout.vue';
import WasloTemplatePreview from '../components/WasloTemplatePreview.vue';
import WasloSelect from '../components/WasloSelect.vue';
import WasloTextArea from '../components/WasloTextArea.vue';
import { useWasloAccountId } from '../composables/useWasloAccountId.js';
import { useWasloI18n } from '../i18n.js';
import { templatesApi, TEMPLATE_ERROR_CODES } from '../api/templates.js';
import { wasloApi } from '../api/client.js';
import {
  blockerPart,
  canChangeCategory,
  canEdit,
  describeTtl,
  editBlockers,
} from '../helpers/template-facts.js';
import {
  BLANK_SAMPLES,
  LIMITS,
  PARAMETER_FORMATS,
  TTL_CHOICES,
  blankDraft,
  buttonListProblem,
  buttonProblems,
  canRenumber,
  canTidyName,
  categorySwitch,
  digitNameWarning,
  draftToEditWire,
  draftToPreview,
  draftToWire,
  duplicateOf,
  fitSamples,
  foldBlankDigits,
  goalDraftToDraft,
  inferFormat,
  isAuthentication,
  isNamed,
  overLimits,
  previewVariables,
  renumberBlanks,
  sampleCount,
  switchCosts,
  templateToDraft,
  tidyName,
  ttlAllowed,
  typingErrors,
  untouchedButton,
  validateDraft,
  wireErrorsToFields,
} from '../helpers/template-draft.js';

const { t } = useWasloI18n();
const route = useRoute();
const router = useRouter();
const accountId = useWasloAccountId();

// The number this wording belongs to, carried in the query by the list page. Templates are
// approved per number, so there is no sensible default to fall back on — without it the form
// cannot be submitted and says so.
const inboxId = computed(() => {
  const raw = route?.query?.inboxId;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? String(parsed) : '';
});

const templateId = computed(() => {
  const raw = route?.params?.templateId;
  return typeof raw === 'string' && raw !== '' ? raw : '';
});

const editing = computed(() => templateId.value !== '');

const draft = ref(blankDraft());
/** What Meta holds right now — its status, its category. Null while creating. */
const existing = ref(null);
const isLoading = ref(false);
const loadFailed = ref('');
/** What the SERVER refused, per field. Kept apart from the live rules — see `fieldErrors`. */
const wireErrors = ref({});
const isSubmitting = ref(false);
const failure = ref('');
const metaMessage = ref('');
const submitted = ref(null);
const saved = ref(false);

/**
 * ── A DRAFT FROM A GOAL (P-4.5) ─────────────────────────────────────────────────────
 *
 * The server answers both languages at once; the editor holds one template at a time. So the
 * answer is kept here, the form is filled with the half in the draft's language, and after she
 * sends it "Make the Arabic one too" fills the other half into a fresh create, with no second
 * call. Nothing is sent for her: the form is hers to change, and Send is still her press.
 */
const goalAnswer = ref(null);
const goalLang = ref('');
const otherMade = ref(false);
/** The library this number already has, for the duplicate warning only. Never blocks. */
const library = ref([]);

/**
 * ── WHICH FIELDS SHE HAS ENGAGED WITH ───────────────────────────────────────────────
 *
 * The whole difference between validating as you type and nagging as you type. A rule may only
 * speak about a field she has been in — or about every field, once she presses Save. See
 * `typingErrors`, which owns the policy; this ref is only the evidence.
 */
const touched = ref({});
/** Save has been pressed at least once. From then on every rule speaks, every time. */
const tried = ref(false);

const ALL_FIELDS = [
  'name',
  'language',
  'category',
  'header',
  'headerSample',
  'body',
  'samples',
  'footer',
  'buttons',
  'ttl',
  'codeExpirationMinutes',
];

const markTouched = field => {
  if (touched.value[field] === true) return;
  touched.value = { ...touched.value, [field]: true };
};

/** Fill the form from one half of the answer, and let every rule speak at once. */
const fillFromGoal = (answer, lang, language) => {
  draft.value = goalDraftToDraft(answer, lang, language);
  touched.value = Object.fromEntries(ALL_FIELDS.map(field => [field, true]));
  tried.value = false;
  wireErrors.value = {};
  failure.value = '';
  metaMessage.value = '';
};

const onDrafted = answer => {
  const language = String(draft.value.language ?? '').trim();
  const lang = language.toLowerCase().startsWith('en') ? 'en' : 'ar';
  goalAnswer.value = answer;
  goalLang.value = lang;
  otherMade.value = false;
  fillFromGoal(answer, lang, language || lang);
};

const otherLang = computed(() => (goalLang.value === 'ar' ? 'en' : 'ar'));
const otherLabel = computed(() =>
  otherLang.value === 'ar'
    ? t('WASLO.TEMPLATES.EDITOR.GOAL.OTHER.AR')
    : t('WASLO.TEMPLATES.EDITOR.GOAL.OTHER.EN')
);
const canMakeOther = computed(
  () =>
    goalAnswer.value !== null && !otherMade.value && submitted.value !== null
);

const makeOther = () => {
  const lang = otherLang.value;
  otherMade.value = true;
  submitted.value = null;
  goalLang.value = lang;
  fillFromGoal(goalAnswer.value, lang, lang);
};

const goalSuggestion = computed(() =>
  goalAnswer.value
    ? {
        category: goalAnswer.value.category,
        reason: goalAnswer.value.categoryReason,
      }
    : null
);

/**
 * Write one field, remember it was touched, and drop the server's refusal of THAT field.
 *
 * Per field, not wholesale: Meta can refuse two fields at once, and clearing both because she
 * fixed one is how the second refusal disappears without being fixed.
 */
const set = (field, value) => {
  markTouched(field);
  if (wireErrors.value[field]) {
    const { [field]: _fixed, ...rest } = wireErrors.value;
    wireErrors.value = rest;
  }
  // P-4.9: `{{١}}` typed on an Arabic keyboard is `{{1}}` before any rule reads it.
  const folded =
    field === 'header' || field === 'body' ? foldBlankDigits(value) : value;
  draft.value = { ...draft.value, [field]: folded };
};

/**
 * `samples` follows the placeholders, so one removed cannot leave a stale example behind and one
 * added never reads from `undefined`. It watches the format too: switching between numbered and
 * named blanks changes HOW MANY the same body has.
 *
 * AN ARRAY OF GETTERS, NOT A GETTER RETURNING AN ARRAY. The second form looks identical and is an
 * infinite loop: the callback assigns a new `draft` object, the getter then builds a FRESH array,
 * Vue sees a changed source, and the watcher re-fires forever ("Maximum recursive updates
 * exceeded"). With an array of getters each source is compared on its own value, so two strings
 * that did not change do not re-trigger. Caught by `specs/templates.spec.js`, which could not mount
 * the editor at all.
 *
 * `inferFormat` rides in the same callback for the same reason — it is a pure function of the
 * body, so the second pass computes the same answer and the watcher settles. NEVER ON AN EDIT:
 * the format Meta already holds is read back by `templateToDraft` and locked.
 */
watch([() => draft.value.body, () => draft.value.parameterFormat], () => {
  const next = editing.value
    ? draft.value
    : { ...draft.value, parameterFormat: inferFormat(draft.value) };
  draft.value = fitSamples(next);
});

const load = async () => {
  if (!editing.value || inboxId.value === '' || accountId.value === null)
    return;
  isLoading.value = true;
  loadFailed.value = '';
  try {
    const result = await templatesApi.one(
      accountId.value,
      inboxId.value,
      templateId.value,
      typeof route?.query?.name === 'string' ? route.query.name : ''
    );
    existing.value = result.template;
    draft.value = templateToDraft(result.template);
    // A template Meta already holds is content she has already written, so every rule may speak
    // about it from the first paint: an over-length line that arrived from WhatsApp Manager is
    // a fact about the thing on screen, not a guess about what she might do next.
    touched.value = Object.fromEntries(ALL_FIELDS.map(field => [field, true]));
  } catch (error) {
    loadFailed.value =
      error?.code === TEMPLATE_ERROR_CODES.NOT_FOUND
        ? t('WASLO.TEMPLATES.EDITOR.GONE')
        : t('WASLO.TEMPLATES.EDITOR.LOAD_FAILED');
  } finally {
    isLoading.value = false;
  }
};

watch([templateId, inboxId, accountId], load, { immediate: true });

/**
 * ── A DRAFT FROM CLAUDE (services spec §8 P-8.4) ─────────────────────────────────────
 *
 * Claude's `draft_approved_message` is a proposal, opened here with `?suggestion=<id>` and filled
 * exactly like a draft from a goal (`onDrafted`): one half now, "Make the Arabic one too" after.
 * Nothing is sent for her; once SHE sends it, the proposal is marked used. A proposal that is not
 * an open approved message leaves the editor as it was.
 */
const fromSuggestion = ref(null);
const loadSuggestion = async () => {
  const id = Number(route?.query?.suggestion);
  if (
    editing.value ||
    accountId.value === null ||
    !Number.isSafeInteger(id) ||
    id <= 0
  )
    return;
  try {
    const s = await wasloApi.getSetupSuggestion(accountId.value, id);
    if (
      s?.kind !== 'approved_message' ||
      s.status !== 'open' ||
      !s.claude?.message
    )
      return;
    fromSuggestion.value = id;
    onDrafted(s.claude.message);
  } catch {
    // The editor works without it: she can still write the message herself.
  }
};
watch([accountId, () => route?.query?.suggestion], loadSuggestion, {
  immediate: true,
});
watch(submitted, value => {
  if (
    value === null ||
    fromSuggestion.value === null ||
    accountId.value === null
  )
    return;
  const id = fromSuggestion.value;
  fromSuggestion.value = null;
  wasloApi
    .applySetupSuggestion(accountId.value, id, { use: true })
    .catch(() => {});
});

/**
 * The library, for the duplicate warning and nothing else.
 *
 * Best effort by design: it WARNS and never refuses, so a read that fails costs the owner a
 * warning rather than her save. `duplicateOf`'s own header writes out why refusing against this
 * list would be a lock-out — it is a mirror Chatwoot syncs and it can hold a template Meta
 * deleted minutes ago.
 */
const loadLibrary = async () => {
  if (inboxId.value === '' || accountId.value === null) return;
  try {
    const result = await templatesApi.list(accountId.value, inboxId.value);
    library.value = Array.isArray(result?.templates) ? result.templates : [];
  } catch {
    library.value = [];
  }
};

watch([inboxId, accountId], loadLibrary, { immediate: true });

const blanks = computed(() => sampleCount(draft.value.body, draft.value));

const formatOptions = computed(() =>
  PARAMETER_FORMATS.map(format => ({
    value: format,
    label: t(`WASLO.TEMPLATES.EDITOR.FORMAT.${format}`),
  }))
);

/**
 * The delivery window, offered as the spans an owner means rather than a box for seconds — and
 * EMPTY for marketing, because Meta documents that range as settable only through the Marketing
 * Messages API. A control that silently does nothing is worse than a sentence saying so.
 */
const ttlLabel = seconds => {
  const described = describeTtl(seconds);
  return t(`WASLO.TEMPLATES.FACTS.WINDOW.${described.unit}`, {
    count: described.value,
  });
};

const ttlOptions = computed(() => {
  const offered = TTL_CHOICES[draft.value.category] ?? [];
  // A template authored in WhatsApp's own manager can carry any value inside Meta's range, not
  // just one of the four shortcuts. Its own value is added so the control SHOWS what the template
  // actually has rather than silently reading as one of ours — which would then be saved.
  const current = Number(draft.value.ttl);
  const extra =
    draft.value.ttl !== '' && !offered.includes(current) ? [current] : [];
  return [
    { value: '', label: t('WASLO.TEMPLATES.EDITOR.WINDOW.DEFAULT') },
    ...[...extra, ...offered].map(seconds => ({
      value: String(seconds),
      label: ttlLabel(seconds),
    })),
  ];
});

/**
 * A delivery window the new category cannot carry is CLEARED rather than stranded.
 *
 * Without this, changing a utility template to marketing leaves a window set that the form no
 * longer shows a control for — so the owner cannot remove it, and the save is refused for a field
 * she cannot see. Found by `specs/template-manager.spec.js`, which could not save the edit at all.
 *
 * It is no longer SILENT: `categorySwitch` reports the same clearing as a cost, and the switch
 * panel names it before she agrees to the change.
 */
watch(
  () => draft.value.category,
  category => {
    if (draft.value.ttl === '') return;
    if (!ttlAllowed(category, draft.value.ttl)) draft.value.ttl = '';
  }
);

const offersWindow = computed(
  () => (TTL_CHOICES[draft.value.category] ?? []).length > 0
);

/** Locked on an approved template — Meta refuses a category change on one. */
const categoryLocked = computed(
  () => editing.value && !canChangeCategory(existing.value ?? {})
);

const changedCategory = computed(
  () =>
    editing.value &&
    !categoryLocked.value &&
    draft.value.category !== (existing.value?.category ?? '')
);

// ── THE RULES, RUN ON EVERY KEYSTROKE ───────────────────────────────────────────────
//
// `changedCategory` is passed through, which nothing used to do: on an unchanged edit the server
// deliberately skips the category, and a client that checked it anyway left an older template
// showing a disabled blank field and an error no control could clear.
const allErrors = computed(() =>
  validateDraft(draft.value, t, editing.value, changedCategory.value)
);

const liveErrors = computed(() =>
  typingErrors(allErrors.value, draft.value, {
    touched: touched.value,
    tried: tried.value,
  })
);

/** What is shown under each control: the live rules, with the server's refusal on top. */
const fieldErrors = computed(() => ({
  ...liveErrors.value,
  ...wireErrors.value,
}));

/** Whether the buttons may speak yet — one gate for the list and every row in it. */
const buttonsEngaged = computed(
  () => tried.value || touched.value.buttons === true
);

const buttonListMessage = computed(() => {
  if (wireErrors.value.buttons) return wireErrors.value.buttons;
  return buttonsEngaged.value ? buttonListProblem(draft.value, t) : '';
});

/**
 * One sentence per button row. A row she has added and written nothing into stays quiet until
 * Save — see `untouchedButton`, which is where that policy is written down.
 */
const buttonMessages = computed(() => {
  if (!buttonsEngaged.value) return [];
  return buttonProblems(draft.value, t).map((message, index) =>
    tried.value || !untouchedButton(draft.value.buttons[index]) ? message : ''
  );
});

// ── THE THREE ONE-PRESS FIXES ───────────────────────────────────────────────────────
//
// `regroup` lives on the button list. These two are the same trade: a rule that is mechanical to
// state and mechanical to satisfy, so the screen satisfies it. Neither invents a word.
const nameFix = computed(() => {
  if (editing.value || !canTidyName(draft.value.name)) return '';
  return tidyName(draft.value.name);
});

const showsNameFix = computed(
  () => nameFix.value !== '' && Boolean(fieldErrors.value.name)
);

const applyNameFix = () => set('name', nameFix.value);

const showsRenumber = computed(
  () =>
    (tried.value || touched.value.body === true) &&
    canRenumber(draft.value.body, draft.value)
);

const applyRenumber = () => set('body', renumberBlanks(draft.value.body));

/**
 * The template Meta already holds that this one repeats word for word.
 *
 * A WARNING, NEVER A REFUSAL, and the reason is `duplicateOf`'s own: the only list this screen
 * has is a mirror Chatwoot syncs, it can hold a template deleted at Meta minutes ago, and
 * refusing a create against it would be a lock-out. So it says which one, and the Save button
 * does not care.
 */
const duplicate = computed(() =>
  touched.value.body === true || tried.value
    ? duplicateOf(draft.value, library.value, templateId.value || null)
    : null
);

/**
 * A named blank carrying a digit — Meta's documented set is small letters and underscores.
 *
 * THE SECOND WARNING ON THIS SCREEN, and it is one for the same reason the duplicate above it
 * is: Meta documents the allowed set but publishes no rejection reason saying a digit is refused,
 * and our own server accepts one. Refusing it here would be a guess that costs the owner a name
 * she may be entitled to use, and would make this form stricter than the boundary it mirrors.
 * `nameWithDigit`'s header has the whole argument. So it says so, and Save does not care.
 */
const nameDigit = computed(() =>
  touched.value.body === true || tried.value
    ? digitNameWarning(draft.value, t)
    : ''
);

/**
 * WHAT IS OVER META'S LIMIT, beside the preview.
 *
 * It cannot be typed — every box carries its ceiling as `maxlength` — so this exists for the one
 * way it still happens: a template written in WhatsApp Manager, read back for editing, longer
 * than this form would have allowed. Chatwoot's own renderer draws the bubble and has no opinion
 * about Meta's limits; this is the line that does.
 */
const tooLong = computed(() =>
  overLimits(draft.value).map(one =>
    t('WASLO.TEMPLATES.EDITOR.PREVIEW.OVER', {
      part: t(`WASLO.TEMPLATES.EDITOR.PART.${one.part}`),
      length: one.length,
      limit: one.limit,
    })
  )
);

// ── ANNOUNCING THE CATEGORY CHANGE BEFORE IT HAPPENS ────────────────────────────────
//
// The picker emits an intention. If the change costs nothing — a blank form, or marketing to
// utility with no window set — it is simply applied, because a confirmation that always says
// "this costs you nothing" is a click, not a safeguard. If it costs something, it is held here
// and named, part by part, and she answers.
const pendingCategory = ref('');

const pendingChange = computed(() =>
  pendingCategory.value
    ? categorySwitch(draft.value, pendingCategory.value)
    : { hides: [], shows: [], clears: [] }
);

const partList = parts =>
  parts
    .map(part => t(`WASLO.TEMPLATES.EDITOR.PART.${part}`))
    .join(t('WASLO.TEMPLATES.EDIT_BLOCKED.JOIN'));

const applyCategory = category => {
  pendingCategory.value = '';
  set('category', category);
};

const chooseCategory = category => {
  if (category === draft.value.category) {
    pendingCategory.value = '';
    return;
  }
  if (switchCosts(categorySwitch(draft.value, category))) {
    pendingCategory.value = category;
    return;
  }
  applyCategory(category);
};

const previewLabels = computed(() => ({
  copyCode: t('WASLO.TEMPLATES.EDITOR.BUTTONS.COPY_CODE_LABEL'),
  authBody: t('WASLO.TEMPLATES.EDITOR.AUTH.PREVIEW_BODY'),
  authSecurity: t('WASLO.TEMPLATES.EDITOR.AUTH.PREVIEW_SECURITY'),
  authExpiry: draft.value.codeExpirationMinutes
    ? t('WASLO.TEMPLATES.EDITOR.AUTH.PREVIEW_EXPIRY', {
        count: draft.value.codeExpirationMinutes,
      })
    : '',
}));

const preview = computed(() =>
  draftToPreview(draft.value, previewLabels.value)
);
const previewValues = computed(() => previewVariables(draft.value));

/** The two words the preview needs that the draft does not carry (P-4.7). */
const previewPartLabels = computed(() => ({
  mediaHeader: t('WASLO.TEMPLATES.EDITOR.PREVIEW.MEDIA_HEADER'),
  seeAll: t('WASLO.TEMPLATES.EDITOR.PREVIEW.SEE_ALL'),
}));

const hasPreview = computed(
  () => isAuthentication(draft.value) || draft.value.body.trim() !== ''
);

const listUrl = computed(() =>
  accountId.value === null
    ? ''
    : frontendURL(`accounts/${accountId.value}/waslo/templates`)
);

const setSample = (index, value) => {
  const samples = [...draft.value.samples];
  samples[index] = value;
  set('samples', samples);
};

const setButtons = value => set('buttons', value);

const handleFailure = error => {
  const wired = wireErrorsToFields(error?.errors, t);
  // `base` belongs to no control — it is the whole submission being refused — so it is lifted
  // out and shown at form level. Leaving it in the map made `Object.keys(...).length` truthy
  // and silenced the banner: a refusal with nothing on screen at all.
  const { base, ...perField } = wired;
  wireErrors.value = perField;
  // Meta's own sentence when Meta is the one that refused, and nothing invented when it is not.
  metaMessage.value = error?.metaError?.message ?? '';
  const refusedOneField =
    error?.code === TEMPLATE_ERROR_CODES.REJECTED &&
    Object.keys(perField).length > 0;
  failure.value =
    base || (refusedOneField ? '' : t('WASLO.TEMPLATES.EDITOR.REFUSED'));
};

const submit = async () => {
  if (isSubmitting.value || inboxId.value === '') return;
  failure.value = '';
  metaMessage.value = '';
  tried.value = true;
  // The same gate as before, and deliberately the same call: what the screen has been saying all
  // along and what refuses the save are one rule set, evaluated once more at the boundary.
  const errors = validateDraft(
    draft.value,
    t,
    editing.value,
    changedCategory.value
  );
  wireErrors.value = {};
  if (Object.keys(errors).length) return;

  isSubmitting.value = true;
  try {
    if (editing.value) {
      await templatesApi.update(
        accountId.value,
        inboxId.value,
        templateId.value,
        draftToEditWire(draft.value, changedCategory.value)
      );
      saved.value = true;
    } else {
      submitted.value = await templatesApi.create(
        accountId.value,
        inboxId.value,
        draftToWire(draft.value)
      );
    }
  } catch (error) {
    handleFailure(error);
  } finally {
    isSubmitting.value = false;
  }
};

const backToList = () => {
  if (listUrl.value) router.push(listUrl.value);
};

/** An edit is refused outright for most statuses — Meta allows only approved, refused, paused. */
const notEditable = computed(
  () => editing.value && existing.value !== null && !canEdit(existing.value)
);

/**
 * THE SECOND REFUSAL, and the one that matters more, because it prevents a destructive save
 * rather than a rejected one.
 *
 * Meta's edit REPLACES ALL COMPONENTS with the request's. This form holds a TEXT header, a body, a
 * footer and four button types; every other part it would simply not send, and Meta would remove
 * it from a live approved template. A picture header read as an empty text box is exactly how that
 * happened. The row already disables its Edit button, and this is the same gate on the screen
 * itself, because a URL can be typed, bookmarked or shared.
 */
const blockersFor = category =>
  existing.value
    ? editBlockers(
        category ? { ...existing.value, category: category } : existing.value
      )
    : [];

/** What is blocked about the template AS META HOLDS IT — true whatever she does next. */
const blockersNow = computed(() => blockersFor(''));

/**
 * ── A RE-CATEGORISATION STRADDLES TWO SHAPES ────────────────────────────────────────
 *
 * Changing what a message is charged as is the one edit where her category wins over Meta's — and
 * the parts are read from the OLD shape while the payload is built for the NEW one. So a part
 * that is unsupported under EITHER is a part this form cannot carry across the change:
 *
 *   a sign-in code → something else   the copy-code button only exists in the sign-in shape, and
 *                                     the ordinary one never emits it
 *   something else → a sign-in code   WhatsApp writes that message itself, so the header, the
 *                                     wording and the buttons are all replaced
 *
 * Refused HERE, the moment she picks the new category, rather than after she has filled in a form
 * — and named, so she knows which part would go.
 */
const blockersAfter = computed(() => {
  if (!changedCategory.value) return [];
  const now = blockersNow.value;
  return blockersFor(draft.value.category).filter(
    one =>
      !now.some(
        seen => seen.reason === one.reason && seen.detail === one.detail
      )
  );
});

const blockers = computed(() => [...blockersNow.value, ...blockersAfter.value]);

const blockedParts = computed(() =>
  blockers.value
    .map(blocker => blockerPart(blocker, t))
    .filter(Boolean)
    // The separator is translated, not typed. It was a hardcoded Arabic comma, so an English
    // reader with two blocking parts read "a picture، a form button".
    .join(t('WASLO.TEMPLATES.EDIT_BLOCKED.JOIN'))
);

/**
 * Two different sentences for two different refusals. "This template cannot be edited here" is
 * about the template; "changing what it is charged as would remove X" is about the act she just
 * chose, and is undone by choosing the old category again.
 */
const blockedReason = computed(() => {
  const first = blockers.value[0];
  if (!first) return '';
  if (blockersNow.value.length === 0) {
    return t('WASLO.TEMPLATES.EDIT_BLOCKED.CHANGING', {
      part: blockedParts.value,
    });
  }
  return t(`WASLO.TEMPLATES.EDIT_BLOCKED.${first.reason}`, {
    part: blockedParts.value,
  });
});

const done = computed(() => saved.value || submitted.value !== null);
</script>

<template>
  <WasloPageLayout
    :header-title="
      editing
        ? t('WASLO.TEMPLATES.EDITOR.TITLE_EDIT')
        : t('WASLO.TEMPLATES.EDITOR.TITLE')
    "
    :back-url="listUrl"
    :back-label="t('WASLO.TEMPLATES.TITLE')"
  >
    <template #body>
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-10 text-n-slate-11"
        data-waslo-template-loading
      >
        <Spinner />
      </div>

      <Banner
        v-else-if="loadFailed"
        color="ruby"
        data-waslo-template-load-failed
      >
        {{ loadFailed }}
      </Banner>

      <!-- ── WHAT META SAID, once there is an answer ─────────────────────────────── -->
      <div
        v-else-if="submitted"
        class="flex flex-col gap-4 px-6 py-6 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
        data-waslo-template-sent
      >
        <div class="flex flex-wrap items-center gap-3">
          <p class="mb-0 text-base font-medium text-n-slate-12">
            {{ t('WASLO.TEMPLATES.EDITOR.SENT.TITLE') }}
          </p>
          <TemplateStatusChip :status="submitted.status" />
        </div>
        <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
          {{
            submitted.status
              ? t('WASLO.TEMPLATES.EDITOR.SENT.BODY')
              : t('WASLO.TEMPLATES.EDITOR.SENT.BODY_UNTOLD')
          }}
        </p>
        <!--
          Meta re-categorises a template it judges to be marketing, and that decides what every
          send costs. Silence here is how an owner is surprised by a bill.
        -->
        <Banner
          v-if="
            submitted.category &&
            submitted.requestedCategory &&
            submitted.category !== submitted.requestedCategory
          "
          color="amber"
          data-waslo-template-recategorised
        >
          {{
            t('WASLO.TEMPLATES.EDITOR.SENT.RECATEGORISED', {
              asked: t(
                `WASLO.TEMPLATES.CATEGORY.${submitted.requestedCategory}`
              ),
              given: submitted.category,
            })
          }}
        </Banner>
        <!--
          THE SAME ANSWER, THE OTHER LANGUAGE (P-4.5). Offered once, after the first is sent, and
          it makes no call: the server already wrote both halves.
        -->
        <div
          v-if="canMakeOther"
          class="flex flex-col gap-1"
          data-waslo-template-goal-other-row
        >
          <div class="flex">
            <Button
              :label="otherLabel"
              icon="i-lucide-languages"
              size="sm"
              data-waslo-template-goal-other
              @click="makeOther"
            />
          </div>
          <p class="mb-0 text-xs leading-5 text-n-slate-10">
            {{ t('WASLO.TEMPLATES.EDITOR.GOAL.OTHER_NOTE') }}
          </p>
        </div>
      </div>

      <!--
        AN EDIT SAYS NOTHING ABOUT THE NEW STATUS, because Meta said nothing about it: its answer
        is `{ success: true }`. Claiming "waiting for review" here would be the same fabrication
        this feature exists against, one door down.
      -->
      <div
        v-else-if="saved"
        class="flex flex-col gap-4 px-6 py-6 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
        data-waslo-template-saved
      >
        <p class="mb-0 text-base font-medium text-n-slate-12">
          {{ t('WASLO.TEMPLATES.EDITOR.SAVED.TITLE') }}
        </p>
        <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
          {{ t('WASLO.TEMPLATES.EDITOR.SAVED.BODY') }}
        </p>
      </div>

      <!-- ── THE FORM ────────────────────────────────────────────────────────────── -->
      <div v-else class="flex flex-col gap-8 pb-10">
        <Banner
          v-if="inboxId === ''"
          color="ruby"
          data-waslo-template-no-sender
        >
          {{ t('WASLO.TEMPLATES.EDITOR.NO_SENDER') }}
        </Banner>

        <Banner v-if="goalAnswer" color="amber" data-waslo-template-goal-banner>
          {{ t('WASLO.TEMPLATES.EDITOR.GOAL.WRITTEN') }}
        </Banner>

        <Banner
          v-if="notEditable"
          color="amber"
          data-waslo-template-not-editable
        >
          {{ t('WASLO.TEMPLATES.EDITOR.NOT_EDITABLE') }}
        </Banner>

        <!--
          A part this form cannot hold. The save is dead and the sentence names the part, because
          "this cannot be edited" sends an owner looking for a fault of her own.
        -->
        <Banner
          v-else-if="blockers.length"
          color="amber"
          data-waslo-template-blocked
        >
          <span dir="auto">{{ blockedReason }}</span>
        </Banner>

        <!--
          Where the template stands with WhatsApp right now, before she changes it. TWO chips:
          the verdict and the rating are different facts and used to share one colour, so an
          approved-but-low template rendered exactly as a refused one.
        -->
        <div
          v-if="editing && existing"
          class="flex flex-wrap items-start gap-3"
          data-waslo-template-current
        >
          <span class="text-sm leading-6 text-n-slate-11">
            {{ t('WASLO.TEMPLATES.EDITOR.CURRENT') }}
          </span>
          <TemplateStatusChip :status="existing.status" />
          <TemplateQualityChip :quality="existing.quality" explain />
        </div>

        <div class="grid items-start gap-6 lg:grid-cols-2">
          <div class="flex flex-col gap-8 min-w-0">
            <!-- ── 0 · A FIRST DRAFT FROM A GOAL (create only, P-4.5) ─────────── -->
            <TemplateGoalStart
              v-if="!editing"
              :disabled="isSubmitting || inboxId === ''"
              :initial-goal="String(route?.query?.goal ?? '')"
              :initial-description="String(route?.query?.describe ?? '')"
              @drafted="onDrafted"
            />
            <!-- ── 1 · WHO IT IS ──────────────────────────────────────────────── -->
            <section class="flex flex-col gap-4" data-waslo-template-about>
              <SettingsHeader
                :heading="t('WASLO.TEMPLATES.EDITOR.ABOUT.HEADING')"
                :description="t('WASLO.TEMPLATES.EDITOR.ABOUT.DESCRIPTION')"
              />
              <!--
                LOCKED ON AN EDIT, with the reason. Meta does not accept a name or a language on
                its edit endpoint — they are the template's identity, and a rename is a new
                template plus a delete. A field whose change is silently dropped is worse than one
                that cannot be touched.
              -->
              <WasloInput
                :model-value="draft.name"
                :label="t('WASLO.TEMPLATES.EDITOR.ABOUT.NAME_LABEL')"
                :hint="
                  editing
                    ? t('WASLO.TEMPLATES.EDITOR.ABOUT.NAME_LOCKED')
                    : t('WASLO.TEMPLATES.EDITOR.ABOUT.NAME_HINT')
                "
                :max-length="editing ? 0 : LIMITS.NAME"
                :message="fieldErrors.name ?? ''"
                message-type="error"
                :disabled="isSubmitting || editing"
                data-waslo-template-name
                @update:model-value="value => set('name', value)"
              />
              <!--
                ONE PRESS, and it only ever rearranges what she typed: lower-cased, spaces and
                punctuation folded to underscores. It is offered only when the name is refused
                AND there is something left to offer — an Arabic shop name folds to nothing, and
                inventing a Latin name she cannot change for thirty days is not a fix.
              -->
              <div v-if="showsNameFix" class="flex">
                <Button
                  :label="
                    t('WASLO.TEMPLATES.EDITOR.ABOUT.NAME_FIX', {
                      name: nameFix,
                    })
                  "
                  icon="i-lucide-wand-2"
                  variant="faded"
                  color="slate"
                  size="sm"
                  :disabled="isSubmitting"
                  data-waslo-template-name-fix
                  @click="applyNameFix"
                />
              </div>
              <WasloInput
                :model-value="draft.language"
                :label="t('WASLO.TEMPLATES.EDITOR.ABOUT.LANGUAGE_LABEL')"
                :hint="
                  editing
                    ? t('WASLO.TEMPLATES.EDITOR.ABOUT.LANGUAGE_LOCKED')
                    : t('WASLO.TEMPLATES.EDITOR.ABOUT.LANGUAGE_HINT')
                "
                :message="fieldErrors.language ?? ''"
                message-type="error"
                :disabled="isSubmitting || editing"
                data-waslo-template-language
                @update:model-value="value => set('language', value)"
              />
              <!--
                THE HIGHEST-CONSEQUENCE FIELD ON THE SCREEN. Three rows, one sentence each, all
                three visible before anything is chosen — see `TemplateCategoryPicker` for why it
                is not a select any more.
              -->
              <TemplateCategoryPicker
                :model-value="draft.category"
                :disabled="isSubmitting || categoryLocked"
                :message="
                  fieldErrors.category ??
                  (categoryLocked
                    ? t('WASLO.TEMPLATES.EDITOR.ABOUT.CATEGORY_LOCKED')
                    : '')
                "
                :message-type="fieldErrors.category ? 'error' : 'info'"
                :suggestion="goalSuggestion"
                @select="chooseCategory"
              />

              <!--
                ── THE ANNOUNCEMENT, BEFORE THE COLLAPSE ────────────────────────────
                Seven fields leave and two arrive. It is named part by part, only for parts she
                has actually written, and the promise that nothing is lost is made because the
                draft really does keep every field.
              -->
              <div
                v-if="pendingCategory"
                class="flex flex-col gap-3 px-4 py-4 rounded-xl bg-n-amber-3"
                data-waslo-template-switch
              >
                <p class="mb-0 text-sm font-medium text-n-amber-12">
                  {{
                    t('WASLO.TEMPLATES.EDITOR.SWITCH.TITLE', {
                      category: t(
                        `WASLO.TEMPLATES.CATEGORY.${pendingCategory}`
                      ),
                    })
                  }}
                </p>
                <p
                  v-if="pendingChange.hides.length"
                  class="mb-0 text-sm leading-6 max-w-[62ch] text-n-amber-11"
                  data-waslo-template-switch-hides
                >
                  {{
                    t('WASLO.TEMPLATES.EDITOR.SWITCH.HIDES', {
                      parts: partList(pendingChange.hides),
                    })
                  }}
                </p>
                <p
                  v-if="pendingChange.shows.length"
                  class="mb-0 text-sm leading-6 max-w-[62ch] text-n-amber-11"
                >
                  {{
                    t('WASLO.TEMPLATES.EDITOR.SWITCH.SHOWS', {
                      parts: partList(pendingChange.shows),
                    })
                  }}
                </p>
                <p
                  v-if="pendingChange.hides.length"
                  class="mb-0 text-sm leading-6 max-w-[62ch] text-n-amber-11"
                >
                  {{
                    t('WASLO.TEMPLATES.EDITOR.SWITCH.KEPT', {
                      from: t(`WASLO.TEMPLATES.CATEGORY.${draft.category}`),
                    })
                  }}
                </p>
                <p
                  v-if="pendingChange.clears.length"
                  class="mb-0 text-sm leading-6 max-w-[62ch] text-n-amber-11"
                  data-waslo-template-switch-clears
                >
                  {{
                    t('WASLO.TEMPLATES.EDITOR.SWITCH.CLEARS', {
                      parts: partList(pendingChange.clears),
                    })
                  }}
                </p>
                <div class="flex flex-wrap gap-2">
                  <!-- The CTA names its outcome, never "Continue". -->
                  <Button
                    :label="
                      t('WASLO.TEMPLATES.EDITOR.SWITCH.CONFIRM', {
                        category: t(
                          `WASLO.TEMPLATES.CATEGORY.${pendingCategory}`
                        ),
                      })
                    "
                    size="sm"
                    data-waslo-template-switch-confirm
                    @click="applyCategory(pendingCategory)"
                  />
                  <Button
                    :label="
                      t('WASLO.TEMPLATES.EDITOR.SWITCH.CANCEL', {
                        from: t(`WASLO.TEMPLATES.CATEGORY.${draft.category}`),
                      })
                    "
                    variant="faded"
                    color="slate"
                    size="sm"
                    data-waslo-template-switch-cancel
                    @click="pendingCategory = ''"
                  />
                </div>
              </div>

              <!--
                A category change re-prices every future send, so it is called out as its own fact
                rather than riding along inside "saved".
              -->
              <Banner
                v-if="changedCategory"
                color="amber"
                data-waslo-template-category-change
              >
                {{ t('WASLO.TEMPLATES.EDITOR.ABOUT.CATEGORY_CHANGING') }}
              </Banner>
            </section>

            <!-- ── A SIGN-IN CODE IS NOT A MESSAGE SHE WRITES ─────────────────── -->
            <section
              v-if="isAuthentication(draft)"
              class="flex flex-col gap-4"
              data-waslo-template-auth
            >
              <SettingsHeader
                :heading="t('WASLO.TEMPLATES.EDITOR.AUTH.HEADING')"
                :description="t('WASLO.TEMPLATES.EDITOR.AUTH.DESCRIPTION')"
              />
              <div class="flex items-start justify-between gap-4">
                <div class="flex flex-col gap-1 min-w-0">
                  <p class="mb-0 text-sm font-medium text-n-slate-12">
                    {{ t('WASLO.TEMPLATES.EDITOR.AUTH.SECURITY_LABEL') }}
                  </p>
                  <p
                    class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
                  >
                    {{ t('WASLO.TEMPLATES.EDITOR.AUTH.SECURITY_NOTE') }}
                  </p>
                </div>
                <Switch
                  :model-value="draft.addSecurityRecommendation"
                  :aria-label="t('WASLO.TEMPLATES.EDITOR.AUTH.SECURITY_LABEL')"
                  data-waslo-template-auth-security
                  @update:model-value="
                    value => set('addSecurityRecommendation', value)
                  "
                />
              </div>
              <WasloInput
                :model-value="draft.codeExpirationMinutes"
                type="number"
                :min="LIMITS.CODE_EXPIRY_MIN"
                :max="LIMITS.CODE_EXPIRY_MAX"
                :label="t('WASLO.TEMPLATES.EDITOR.AUTH.EXPIRY_LABEL')"
                :hint="t('WASLO.TEMPLATES.EDITOR.AUTH.EXPIRY_HINT')"
                :message="fieldErrors.codeExpirationMinutes ?? ''"
                message-type="error"
                :disabled="isSubmitting"
                data-waslo-template-auth-expiry
                @update:model-value="
                  value => set('codeExpirationMinutes', value)
                "
              />
            </section>

            <!-- ── 2 · META'S ORDER: HEADER → BODY → FOOTER → BUTTONS ─────────── -->
            <template v-else>
              <section class="flex flex-col gap-4" data-waslo-template-message>
                <SettingsHeader
                  :heading="t('WASLO.TEMPLATES.EDITOR.MESSAGE.HEADING')"
                  :description="
                    t(
                      'WASLO.TEMPLATES.EDITOR.MESSAGE.DESCRIPTION',
                      BLANK_SAMPLES
                    )
                  "
                />
                <!--
                  ON AN EDIT ONLY. Numbered or named blanks are not interchangeable — each has
                  its own example key on the wire — and Meta's own two reference pages disagree
                  about whether this may change after approval, so an existing template shows
                  what it is and cannot move it. On a CREATE the control is gone: the format is
                  read off the blanks she writes, which is what the field means. See
                  `inferFormat`.
                -->
                <WasloSelect
                  v-if="editing"
                  :model-value="draft.parameterFormat"
                  :label="t('WASLO.TEMPLATES.EDITOR.FORMAT_LABEL')"
                  :options="formatOptions"
                  :message="t('WASLO.TEMPLATES.EDITOR.FORMAT_LOCKED')"
                  disabled
                  data-waslo-template-format
                />
                <WasloInput
                  :model-value="draft.header"
                  :label="t('WASLO.TEMPLATES.EDITOR.MESSAGE.HEADER_LABEL')"
                  :hint="t('WASLO.TEMPLATES.EDITOR.MESSAGE.HEADER_HINT')"
                  :max-length="LIMITS.HEADER"
                  :message="fieldErrors.header ?? ''"
                  message-type="error"
                  :disabled="isSubmitting"
                  data-waslo-template-header
                  @update:model-value="value => set('header', value)"
                />
                <!-- Meta allows ONE blank in the line above, and requires an example for it. -->
                <WasloInput
                  v-if="draft.header.includes('{{')"
                  :model-value="draft.headerSample"
                  :label="t('WASLO.TEMPLATES.EDITOR.MESSAGE.HEADER_SAMPLE')"
                  :max-length="LIMITS.SAMPLE"
                  :message="fieldErrors.headerSample ?? ''"
                  message-type="error"
                  :disabled="isSubmitting"
                  data-waslo-template-header-sample
                  @update:model-value="value => set('headerSample', value)"
                />
                <WasloTextArea
                  :model-value="draft.body"
                  :label="t('WASLO.TEMPLATES.EDITOR.MESSAGE.BODY_LABEL')"
                  :hint="
                    t(
                      `WASLO.TEMPLATES.EDITOR.MESSAGE.BODY_HINT_${
                        isNamed(draft) ? 'NAMED' : 'NUMBERED'
                      }`,
                      BLANK_SAMPLES
                    )
                  "
                  :max-length="LIMITS.BODY"
                  :message="fieldErrors.body ?? ''"
                  message-type="error"
                  :disabled="isSubmitting"
                  data-waslo-template-body
                  @update:model-value="value => set('body', value)"
                />
                <!--
                  THE SECOND ONE-PRESS FIX. `{{1}} … {{3}}` is one of Meta's own published
                  refusal reasons and nothing about it needs a human decision — the blanks keep
                  their order and lose the gap.
                -->
                <div v-if="showsRenumber" class="flex">
                  <Button
                    :label="t('WASLO.TEMPLATES.EDITOR.MESSAGE.RENUMBER')"
                    icon="i-lucide-list-ordered"
                    variant="faded"
                    color="slate"
                    size="sm"
                    :disabled="isSubmitting"
                    data-waslo-template-body-renumber
                    @click="applyRenumber"
                  />
                </div>
                <!--
                  THE FORMAT AS A CONSEQUENCE, not a question: it is read off the blanks, so the
                  composer prints what it read rather than asking her to choose it. The same
                  statement the menu editor makes about three-options-are-buttons.
                -->
                <p
                  v-if="!editing && blanks > 0"
                  class="mb-0 text-xs leading-5 max-w-[62ch] text-n-slate-10"
                  data-waslo-template-format-read
                >
                  {{
                    t(
                      `WASLO.TEMPLATES.EDITOR.FORMAT_READ.${
                        isNamed(draft) ? 'NAMED' : 'POSITIONAL'
                      }`,
                      { ...BLANK_SAMPLES, count: blanks }
                    )
                  }}
                </p>

                <!--
                  The example values, directly under the body that created the blanks. Meta
                  REQUIRES one per blank — a template with blanks and no examples is refused
                  outright — and they are also what the preview substitutes, so filling them in
                  is the same act as seeing what the customer will read.
                -->
                <div
                  v-if="blanks > 0"
                  class="flex flex-col gap-4 ps-4 border-s border-n-weak"
                  data-waslo-template-samples
                >
                  <p
                    class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
                  >
                    {{ t('WASLO.TEMPLATES.EDITOR.BLANKS.DESCRIPTION') }}
                  </p>
                  <WasloInput
                    v-for="index in blanks"
                    :key="index"
                    :model-value="draft.samples[index - 1] ?? ''"
                    :label="
                      t('WASLO.TEMPLATES.EDITOR.BLANKS.LABEL', {
                        number: index,
                      })
                    "
                    :max-length="LIMITS.SAMPLE"
                    :disabled="isSubmitting"
                    :data-waslo-template-sample="index"
                    @update:model-value="value => setSample(index - 1, value)"
                  />
                  <p
                    v-if="fieldErrors.samples"
                    class="mb-0 text-sm leading-6 max-w-[62ch] text-n-ruby-9"
                    data-waslo-template-samples-error
                  >
                    {{ fieldErrors.samples }}
                  </p>
                </div>

                <WasloInput
                  :model-value="draft.footer"
                  :label="t('WASLO.TEMPLATES.EDITOR.MESSAGE.FOOTER_LABEL')"
                  :hint="t('WASLO.TEMPLATES.EDITOR.MESSAGE.FOOTER_HINT')"
                  :max-length="LIMITS.FOOTER"
                  :message="fieldErrors.footer ?? ''"
                  message-type="error"
                  :disabled="isSubmitting"
                  data-waslo-template-footer
                  @update:model-value="value => set('footer', value)"
                />

                <!--
                  META REFUSES A REPEAT — the same body AND the same line under it. It WARNS and
                  never refuses here, because the only library this screen has is a mirror that
                  can hold a template deleted at Meta minutes ago. See `duplicateOf`.
                -->
                <p
                  v-if="duplicate"
                  class="mb-0 text-sm leading-6 max-w-[62ch] text-n-amber-11"
                  data-waslo-template-duplicate
                >
                  {{
                    t('WASLO.TEMPLATES.EDITOR.ERRORS.DUPLICATE', {
                      name: duplicate.name,
                    })
                  }}
                </p>
                <!--
                  A DIGIT IN A NAMED BLANK, which Meta's own sentence leaves out of the named
                  format ("lowercase characters and underscores"). It WARNS and never refuses:
                  Meta publishes no rejection reason for a digit and our server accepts one, so
                  a refusal here would lock her out of a name on a guess. See `nameWithDigit`.
                -->
                <p
                  v-if="nameDigit"
                  class="mb-0 text-sm leading-6 max-w-[62ch] text-n-amber-11"
                  data-waslo-template-name-digit
                >
                  {{ nameDigit }}
                </p>
              </section>

              <section class="flex flex-col gap-4">
                <SettingsHeader
                  :heading="t('WASLO.TEMPLATES.EDITOR.BUTTONS.HEADING')"
                />
                <TemplateButtonList
                  :buttons="draft.buttons"
                  :message="buttonListMessage"
                  :problems="buttonMessages"
                  :disabled="isSubmitting"
                  @update:buttons="setButtons"
                />
              </section>
            </template>

            <!-- ── 3 · HOW LONG WHATSAPP KEEPS TRYING ─────────────────────────── -->
            <section class="flex flex-col gap-4" data-waslo-template-window>
              <SettingsHeader
                :heading="t('WASLO.TEMPLATES.EDITOR.WINDOW.HEADING')"
                :description="t('WASLO.TEMPLATES.EDITOR.WINDOW.DESCRIPTION')"
              />
              <WasloSelect
                v-if="offersWindow"
                :model-value="draft.ttl"
                :label="t('WASLO.TEMPLATES.EDITOR.WINDOW.LABEL')"
                :options="ttlOptions"
                :message="fieldErrors.ttl ?? ''"
                :message-type="fieldErrors.ttl ? 'error' : 'info'"
                :disabled="isSubmitting"
                data-waslo-template-ttl
                @update:model-value="value => set('ttl', value)"
              />
              <!--
                Marketing has a range at Meta and it is settable only through the Marketing
                Messages API, not the one this product uses. A control here would silently do
                nothing, so there is a sentence instead.
              -->
              <p
                v-else
                class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11"
                data-waslo-template-window-unavailable
              >
                {{ t('WASLO.TEMPLATES.EDITOR.WINDOW.MARKETING') }}
              </p>
            </section>
          </div>

          <!-- ── THE LIVE PREVIEW ────────────────────────────────────────────────── -->
          <section
            class="flex flex-col gap-4 lg:sticky lg:top-4"
            data-waslo-template-preview
          >
            <SettingsHeader
              :heading="t('WASLO.TEMPLATES.EDITOR.PREVIEW.HEADING')"
              :description="t('WASLO.TEMPLATES.EDITOR.PREVIEW.DESCRIPTION')"
            />
            <div
              class="flex justify-center px-5 py-6 outline outline-1 -outline-offset-1 outline-n-container rounded-xl bg-n-solid-2"
            >
              <p
                v-if="!hasPreview"
                class="mb-0 text-sm leading-6 text-center max-w-[42ch] text-n-slate-10"
                data-waslo-template-preview-empty
              >
                {{ t('WASLO.TEMPLATES.EDITOR.PREVIEW.EMPTY') }}
              </p>
              <WasloTemplatePreview
                v-else
                :template="preview"
                :variables="previewValues"
                :lang="draft.language"
                :labels="previewPartLabels"
              />
            </div>
            <!--
              WHAT THE PREVIEW CANNOT SAY. It draws the bubble and has no opinion about
              Meta's ceilings, and a part that is over one of them cannot be typed here — so this
              only ever fires for wording written in WhatsApp Manager and read back for editing.
              Ruby, with the count, the way the menu editor draws an over-length option.
            -->
            <ul
              v-if="tooLong.length"
              class="flex flex-col gap-1 mb-0 list-none"
              data-waslo-template-over-limits
            >
              <li
                v-for="(line, index) in tooLong"
                :key="index"
                class="text-sm leading-6 max-w-[62ch] text-n-ruby-9"
              >
                {{ line }}
              </li>
            </ul>
            <p
              v-if="isAuthentication(draft)"
              class="mb-0 text-xs leading-5 max-w-[62ch] text-n-slate-10"
              data-waslo-template-auth-preview-note
            >
              {{ t('WASLO.TEMPLATES.EDITOR.AUTH.PREVIEW_NOTE') }}
            </p>
          </section>
        </div>
      </div>
    </template>

    <!--
      THE BAR IS ALWAYS PROVIDED, and that is not carelessness. `WasloPageLayout`'s
      `showFooter` reads `useSlots().footer` — a plain property, not a reactive dependency — so
      a `v-if` on this template would leave the computed stale and dock an EMPTY bar after a
      submission. It carries the submit before there is an answer and the way back after.
    -->
    <template #footer>
      <div class="flex flex-col gap-3">
        <template v-if="done">
          <div class="flex items-center justify-end">
            <Button
              :label="t('WASLO.TEMPLATES.EDITOR.SENT.BACK')"
              size="sm"
              data-waslo-template-sent-back
              @click="backToList"
            />
          </div>
        </template>
        <template v-else>
          <Banner
            v-if="metaMessage"
            color="ruby"
            data-waslo-template-meta-error
          >
            <bdi>{{ metaMessage }}</bdi>
          </Banner>
          <Banner v-if="failure" color="ruby" data-waslo-template-failure>
            {{ failure }}
          </Banner>
          <div class="flex items-center justify-between gap-3">
            <p class="mb-0 text-xs leading-5 max-w-[62ch] text-n-slate-10">
              {{
                editing
                  ? t('WASLO.TEMPLATES.EDITOR.EDIT_NOTE')
                  : t('WASLO.TEMPLATES.EDITOR.SUBMIT_NOTE')
              }}
            </p>
            <Button
              class="shrink-0"
              :label="
                isSubmitting
                  ? t('WASLO.TEMPLATES.EDITOR.SUBMITTING')
                  : editing
                    ? t('WASLO.TEMPLATES.EDITOR.SAVE')
                    : t('WASLO.TEMPLATES.EDITOR.SUBMIT')
              "
              icon="i-lucide-send"
              :is-loading="isSubmitting"
              :disabled="
                isSubmitting ||
                inboxId === '' ||
                notEditable ||
                isLoading ||
                blockers.length > 0
              "
              data-waslo-template-submit
              @click="submit"
            />
          </div>
        </template>
      </div>
    </template>
  </WasloPageLayout>
</template>
